import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPrint } from "react-icons/fa";
import { useAuth } from "../../../utils/AuthContext";
import PageHeading from "../../PageHeading";
import TableSkeleton from "../../TableSkeleton";
import { printMonthlyReport } from "../../../utils/printMonthlyReports";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const MonthlyReports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("month-end");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [loading, setLoading] = useState(false);
  const [monthEndData, setMonthEndData] = useState({ rows: [], grandTotal: {} });
  const [invoiceGroups, setInvoiceGroups] = useState([]);
  const [assetData, setAssetData] = useState(null);
  const [y, m] = selectedMonth.split("-");

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "Pathology Accounts" },
      { label: "Monthly Reports" },
    ],
    []
  );

  const fetchActiveReport = useCallback(async () => {
    setLoading(true);
    const headers = { "x-api-key": API_KEY };
    try {
      if (activeTab === "month-end") {
        const res = await axios.get(
          `${BASE_URL}/pathology-accounts/month-end-refer?year=${y}&month=${m}`,
          { headers }
        );
        setMonthEndData(res.data);
      } else if (activeTab === "invoice-statement") {
        const res = await axios.get(
          `${BASE_URL}/pathology-accounts/invoice-statement-refer?year=${y}&month=${m}`,
          { headers }
        );
        setInvoiceGroups(res.data.groups || []);
      } else if (activeTab === "asset-summary") {
        const res = await axios.get(
          `${BASE_URL}/pathology-accounts/asset-daily-summary?year=${y}&month=${m}`,
          { headers }
        );
        setAssetData(res.data.data);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load report data.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, y, m]);

  useEffect(() => {
    fetchActiveReport();
  }, [fetchActiveReport]);

  const monthLabel = useMemo(() => {
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    const lastDay = new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
    return `01 ${d.toLocaleString("en-US", { month: "short" })} ${y} – ${lastDay} ${d.toLocaleString("en-US", { month: "short" })} ${y}`;
  }, [y, m]);

  const handlePrint = () => {
    printMonthlyReport({
      activeTab,
      monthLabel,
      generatedTime: new Date().toLocaleString("en-GB"),
      monthEndData,
      invoiceGroups,
      assetData,
      printedBy: user?.name || "Accounts",
    });
  };

  const skeletonColumns = [
    { width: 50, height: 16 },
    { width: 150, height: 16 },
    { width: 90, height: 16 },
    { width: 50, height: 16, className: "text-center" },
    { width: 90, height: 16, className: "text-right" },
    { width: 90, height: 16, className: "text-right" },
    { width: 90, height: 16, className: "text-right" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" autoClose={2500} />
      <div className="print-hidden">
        <PageHeading title="Monthly Accounts Report" breadcrumbs={breadcrumbs} />
      </div>

      {/* --- Filter & Action Bar --- */}
      <div className="bg-white shadow-sm rounded-lg mb-6 print-hidden">
        <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
          {/* Tab Selection Buttons */}
          <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-lg border border-gray-200">
            <button
              onClick={() => setActiveTab("month-end")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                activeTab === "month-end"
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Month End Refer
            </button>
            <button
              onClick={() => setActiveTab("invoice-statement")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                activeTab === "invoice-statement"
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Invoice Statement
            </button>
            <button
              onClick={() => setActiveTab("asset-summary")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                activeTab === "asset-summary"
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Asset Summary
            </button>
          </div>

          {/* Month Input & Print Button */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-600">Month:</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border rounded-md p-1.5 text-sm outline-none bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-1.5 text-xs font-bold rounded-md flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
            >
              <FaPrint size={14} /> Print Report
            </button>
          </div>
        </div>
      </div>

      {/* --- Dynamic Summary Cards --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6 print-hidden">
        {activeTab === "month-end" && (
          <>
            <div className="bg-white p-5 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Agents Active
              </p>
              <p className="text-2xl font-black text-blue-600 mt-0.5">
                {monthEndData.grandTotal?.totalAgents || 0}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-gray-700 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Bills Count
              </p>
              <p className="text-2xl font-black text-gray-800 mt-0.5">
                {monthEndData.grandTotal?.totalBills || 0}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-orange-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Net Commission Generated
              </p>
              <p className="text-2xl font-black text-orange-600 mt-0.5">
                ৳ {Number(monthEndData.grandTotal?.totalNetCommission || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-emerald-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Withdrawn Paid
              </p>
              <p className="text-2xl font-black text-emerald-600 mt-0.5">
                ৳ {Number(monthEndData.grandTotal?.totalPaid || 0).toFixed(2)}
              </p>
            </div>
          </>
        )}

        {activeTab === "invoice-statement" && (
          <>
            <div className="bg-white p-5 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Referral Agents Grouped
              </p>
              <p className="text-2xl font-black text-blue-600 mt-0.5">
                {invoiceGroups.length}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-gray-700 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Period Invoices
              </p>
              <p className="text-2xl font-black text-gray-800 mt-0.5">
                {invoiceGroups.reduce((s, g) => s + (g.bills?.length || 0), 0)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-orange-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                This Period Commission
              </p>
              <p className="text-2xl font-black text-orange-600 mt-0.5">
                ৳{" "}
                {invoiceGroups
                  .reduce((s, g) => s + (g.thisPeriodNetCom || 0), 0)
                  .toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-red-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Net Disbursed
              </p>
              <p className="text-2xl font-black text-red-600 mt-0.5">
                ৳ {invoiceGroups.reduce((s, g) => s + (g.paid || 0), 0).toFixed(2)}
              </p>
            </div>
          </>
        )}

        {activeTab === "asset-summary" && assetData && (
          <>
            <div className="bg-white p-5 rounded-xl border-l-4 border-emerald-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Cash Received
              </p>
              <p className="text-2xl font-black text-emerald-600 mt-0.5">
                ৳ {Number(assetData.totalReceive || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-red-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Expense & Paid
              </p>
              <p className="text-2xl font-black text-red-600 mt-0.5">
                ৳ {Number(assetData.totalPayment || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-gray-700 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Opening Balance
              </p>
              <p className="text-2xl font-black text-gray-800 mt-0.5">
                ৳ {Math.abs(assetData.openingBalance || 0).toFixed(0)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Net Period Balance
              </p>
              <p className="text-2xl font-black text-blue-600 mt-0.5">
                ৳ {Number(assetData.balanceInDuration || 0).toLocaleString()}
              </p>
            </div>
          </>
        )}
      </div>

      <div className="print-hidden bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        
        {/* ----------------- TAB 1: Month End Refer ----------------- */}
        {activeTab === "month-end" && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">CHART ACC ID</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">AGENT / REFER</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-center">MOBILE</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-center">BILLS</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-right">NET COMMISSION</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-right">PAID</th>
                  <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-right">DUE</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <TableSkeleton rowCount={8} columns={skeletonColumns} />
                ) : monthEndData.rows?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-10 text-center text-gray-400 text-xs">
                      No agent transactions in this period.
                    </td>
                  </tr>
                ) : (
                  monthEndData.rows?.map((row) => (
                    <tr key={row.chartAccId} className="border-b last:border-none hover:bg-gray-50 transition-colors">
                      <td className="p-3 font-bold text-blue-600">{row.chartAccId}</td>
                      <td className="p-3 font-bold text-gray-800 uppercase text-xs">{row.agentName}</td>
                      <td className="p-3 text-center text-gray-500 font-mono text-xs">{row.mobile}</td>
                      <td className="p-3 text-center font-bold text-gray-700">{row.billsCount}</td>
                      <td className="p-3 text-right font-black text-gray-900">৳ {row.netCommission.toFixed(2)}</td>
                      <td className="p-3 text-right font-bold text-emerald-600">৳ {row.paidAmount.toFixed(2)}</td>
                      <td className="p-3 text-right font-bold text-red-600">৳ {row.dueAmount.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {monthEndData.rows?.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-black text-gray-900 border-t-2 border-gray-300">
                    <td className="p-3 font-bold" colSpan="3">
                      Grand Total ({monthEndData.grandTotal?.totalAgents} agents)
                    </td>
                    <td className="p-3 text-center font-bold">{monthEndData.grandTotal?.totalBills}</td>
                    <td className="p-3 text-right font-black">৳ {monthEndData.grandTotal?.totalNetCommission?.toFixed(2)}</td>
                    <td className="p-3 text-right text-emerald-600 font-black">৳ {monthEndData.grandTotal?.totalPaid?.toFixed(2)}</td>
                    <td className="p-3 text-right text-red-600 font-black">৳ {monthEndData.grandTotal?.totalDue?.toFixed(2)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* ----------------- TAB 2: Invoice Statement (ফিক্সড লোডারসহ) ----------------- */}
        {activeTab === "invoice-statement" && (
          <div className="p-4 space-y-5">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                    <div className="h-24 bg-gray-100 rounded w-full animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : invoiceGroups.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-xs">
                No refer bills recorded in this period.
              </div>
            ) : (
              invoiceGroups.map((grp) => (
                <div key={grp.chartAccId} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-3 py-2 border-b border-gray-200 font-bold text-gray-800 text-xs flex justify-between items-center">
                    <span>
                      {grp.agentName}{" "}
                      <span className="font-normal text-gray-500">
                        (Chart Acc Id: {grp.chartAccId} · Mobile: {grp.mobile})
                      </span>
                    </span>
                  </div>
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 uppercase text-[10px] font-bold border-b border-gray-200">
                        <th className="p-2.5">INV NO</th>
                        <th className="p-2.5">DATE</th>
                        <th className="p-2.5">PATIENT</th>
                        <th className="p-2.5 text-right">NET AMT</th>
                        <th className="p-2.5 text-right">NET COM</th>
                        <th className="p-2.5 text-center">STATUS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {grp.bills.map((b, i) => (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          <td className="p-2.5 font-bold text-gray-800">{b.invNo}</td>
                          <td className="p-2.5 text-gray-600">{new Date(b.date).toLocaleDateString("en-GB")}</td>
                          <td className="p-2.5 uppercase font-medium text-gray-700">{b.patient?.toLowerCase()}</td>
                          <td className="p-2.5 text-right font-semibold text-gray-700">৳ {b.netAmt.toFixed(2)}</td>
                          <td className="p-2.5 text-right font-black text-orange-600">৳ {b.netCom.toFixed(2)}</td>
                          <td className="p-2.5 text-center">
                            <span
                              className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                b.status === "POSTED" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="bg-gray-50 p-2.5 text-xs font-bold text-gray-700 flex flex-wrap justify-between items-center gap-2 border-t border-gray-200">
                    <span className="font-extrabold text-gray-900">
                      Sub-total (Net Commission): ৳ {grp.thisPeriodNetCom.toFixed(2)}
                    </span>
                    <span className="text-gray-500 font-semibold">
                      Opening ৳ {grp.opening.toFixed(0)}  ·  This period Net Com ৳ {grp.thisPeriodNetCom.toFixed(2)}  ·  Paid ৳ {grp.paid.toFixed(2)}  ·  {" "}
                      <span className="text-blue-700 font-black">Closing ৳ {grp.closing.toFixed(0)}</span>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ----------------- TAB 3: Asset Summary ----------------- */}
        {activeTab === "asset-summary" && assetData && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200 text-gray-600 text-[11px] uppercase font-bold">
                  <th className="p-3">NARRATION</th>
                  <th className="p-3 text-right w-44">DEBIT</th>
                  <th className="p-3 text-right w-44">CREDIT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="p-3 font-semibold text-gray-800">Total Receive</td>
                  <td className="p-3 text-right font-black text-emerald-600">৳ {assetData.totalReceive.toFixed(2)}</td>
                  <td className="p-3 text-right text-gray-300 font-mono">—</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="p-3 font-semibold text-gray-800">
                    Total Payment <span className="text-gray-400 font-normal">(agent payout + others expense)</span>
                  </td>
                  <td className="p-3 text-right text-gray-300 font-mono">—</td>
                  <td className="p-3 text-right font-black text-red-600">৳ {assetData.totalPayment.toFixed(2)}</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="p-3 font-semibold text-gray-800">
                    Opening <span className="text-gray-400 font-normal">(all activity before this period)</span>
                  </td>
                  <td className="p-3 text-right text-gray-300 font-mono">—</td>
                  <td className="p-3 text-right font-black text-gray-700">৳ {Math.abs(assetData.openingBalance).toFixed(0)}</td>
                </tr>
                <tr className="bg-gray-50/80 font-black">
                  <td className="p-3 text-gray-900">
                    Balance in duration <span className="text-gray-500 font-normal text-xs">(Receive – Payment)</span>
                  </td>
                  <td colSpan="2" className="p-3 text-right text-emerald-600 font-black">৳ {assetData.balanceInDuration.toLocaleString()}</td>
                </tr>
                <tr className="bg-gray-100 font-black border-t-2 border-gray-300">
                  <td className="p-3 text-gray-900">
                    Balance with opening <span className="text-gray-500 font-normal text-xs">(Opening + Balance in duration)</span>
                  </td>
                  <td colSpan="2" className="p-3 text-right text-emerald-700 font-black text-base">৳ {assetData.balanceWithOpening.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
  );
};

export default MonthlyReports;